import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ClearAll, Inventory2, Refresh } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import API from '../services/api';
import MainText from '../components/MainText';
import CustomSnackBar from '../components/CustomSnackBar';
import { addTab } from '../tabs/tabsSlice';

const todayLocal = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const firstDayOfMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};

const money = (value) =>
  new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(Number(value || 0));

const number = (value) => Number(value || 0).toLocaleString('es-GT');

const formatDate = (value) => {
  if (!value) return '-';
  const text = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text.slice(8)}/${text.slice(5, 7)}/${text.slice(0, 4)}` : text;
};

function Metric({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ mt: 0.5, fontSize: 20, fontWeight: 700 }}>{value}</Typography>
    </Paper>
  );
}

const productLabel = (product) => {
  if (!product) return '';
  const code = product.code ? `${product.code} - ` : '';
  return `${code}${product.description || ''}`;
};

export default function KardexHomogeneo() {
  const dispatch = useDispatch();
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [products, setProducts] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [productInput, setProductInput] = useState('');
  const [filters, setFilters] = useState({ date_from: firstDayOfMonth(), date_to: todayLocal(), category_id: '' });
  const [payload, setPayload] = useState({ summary: {}, groups: [], movements: [], mode: 'HOMOGENEO', warning: '' });
  const [loading, setLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  const showSnack = useCallback((message, severity = 'info') => {
    setSnack({ open: true, message, severity });
  }, []);

  useEffect(() => {
    dispatch(addTab({ label: 'Kardex homogeneo', path: '/orders/kardex/homogeneo' }));
  }, [dispatch]);

  useEffect(() => {
    API.get('invoice/suppliers/')
      .then(({ data }) => setSuppliers(Array.isArray(data) ? data : data?.results || []))
      .catch(() => setSuppliers([]));
    API.get('catalogs/categories/')
      .then(({ data }) => setCategories(Array.isArray(data) ? data : data?.results || []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const query = productInput.trim();
    if (!supplier?.id || query.length < 2) {
      setProductOptions(products);
      return undefined;
    }
    const controller = new AbortController();
    let mounted = true;
    setProductLoading(true);
    API.get('product/', {
      params: {
        supplier_id: supplier.id,
        search: query,
        active: true,
        limit: 12,
      },
      signal: controller.signal,
    })
      .then(({ data }) => {
        if (!mounted) return;
        const list = Array.isArray(data) ? data : data?.results || [];
        const selectedIds = new Set(products.map((item) => item.id));
        setProductOptions([...products, ...list.filter((item) => !selectedIds.has(item.id))]);
      })
      .catch((error) => {
        if (error?.code !== 'ERR_CANCELED') {
          setProductOptions(products);
        }
      })
      .finally(() => {
        if (mounted) setProductLoading(false);
      });
    return () => {
      mounted = false;
      controller.abort();
    };
  }, [productInput, products, supplier?.id]);

  const fetchData = useCallback(async () => {
    if (!supplier?.id) {
      showSnack('Selecciona un proveedor', 'warning');
      return;
    }
    if (!products.length) {
      showSnack('Selecciona al menos un producto', 'warning');
      return;
    }
    setLoading(true);
    try {
      const { data } = await API.get('kardex/homogeneo/', {
        params: {
          provider_id: supplier.id,
          product_ids: products.map((item) => item.id).join(','),
          category_id: filters.category_id || undefined,
          date_from: filters.date_from,
          date_to: filters.date_to,
        },
      });
      setPayload(data || { summary: {}, groups: [], movements: [], mode: 'HOMOGENEO', warning: '' });
    } catch (error) {
      console.error('Error cargando kardex homogeneo', error);
      showSnack(error?.response?.data?.detail || 'No se pudo cargar el Kardex homogeneo', 'error');
      setPayload({ summary: {}, groups: [], movements: [], mode: 'HOMOGENEO', warning: '' });
    } finally {
      setLoading(false);
    }
  }, [filters, products, showSnack, supplier]);

  const clear = () => {
    setSupplier(null);
    setProducts([]);
    setProductInput('');
    setProductOptions([]);
    setPayload({ summary: {}, groups: [], movements: [], mode: 'HOMOGENEO', warning: '' });
  };

  const movementColumns = useMemo(() => [
    { field: 'date', headerName: 'Fecha', minWidth: 105, valueFormatter: (value) => formatDate(value) },
    { field: 'product_description', headerName: 'Producto', minWidth: 240, flex: 1 },
    { field: 'format', headerName: 'Formato', minWidth: 120 },
    { field: 'quantity_received', headerName: 'Recibido', minWidth: 120, valueFormatter: (value) => number(value) },
    { field: 'price', headerName: 'Precio', minWidth: 120, valueFormatter: (value) => money(value) },
    { field: 'average_cost', headerName: 'Costo prom.', minWidth: 120, valueFormatter: (value) => money(value) },
    { field: 'invoice_number', headerName: 'Factura', minWidth: 130 },
    { field: 'order_codes', headerName: 'Ordenes', minWidth: 180, renderCell: ({ row }) => (row.order_codes || []).join(', ') },
  ], []);

  const groupColumns = useMemo(() => [
    { field: 'product_description', headerName: 'Producto', minWidth: 250, flex: 1 },
    { field: 'presentation', headerName: 'Presentacion', minWidth: 140 },
    { field: 'unit', headerName: 'Unidad', minWidth: 100 },
    { field: 'quantity', headerName: 'Cantidad', minWidth: 120, valueFormatter: (value) => number(value) },
    { field: 'average_cost', headerName: 'Costo prom.', minWidth: 130, valueFormatter: (value) => money(value) },
    { field: 'cost_variation', headerName: 'Variacion', minWidth: 120, valueFormatter: (value) => money(value) },
    { field: 'total_value', headerName: 'Total', minWidth: 130, valueFormatter: (value) => money(value) },
  ], []);

  const summary = payload.summary || {};
  const mode = payload.mode || 'HOMOGENEO';

  return (
    <>
      <MainText
        title="Kardex homogeneo"
        subtitle="Trazabilidad de grupos de productos relacionados del mismo proveedor."
        icon={Inventory2}
        width="auto"
      />

      <Paper variant="outlined" sx={{ mt: 1.5, p: 2, borderRadius: 1 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.25} alignItems={{ lg: 'center' }}>
          <Autocomplete
            options={suppliers}
            value={supplier}
            onChange={(_, value) => {
              setSupplier(value);
              setProducts([]);
              setProductOptions([]);
            }}
            getOptionLabel={(option) => option?.companyName || option?.legalName || ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => <TextField {...params} size="small" label="Proveedor" />}
            sx={{ minWidth: { xs: '100%', lg: 280 } }}
          />
          <Autocomplete
            multiple
            options={productOptions}
            value={products}
            inputValue={productInput}
            onInputChange={(_, value) => setProductInput(value)}
            onChange={(_, value) => setProducts(value)}
            getOptionLabel={productLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={productLoading}
            disabled={!supplier?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Productos"
                placeholder="Buscar por nombre o codigo"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {productLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ minWidth: { xs: '100%', lg: 420 }, flex: 1 }}
          />
          <TextField
            select
            size="small"
            label="Categoria"
            value={filters.category_id}
            onChange={(event) => setFilters((prev) => ({ ...prev, category_id: event.target.value }))}
            SelectProps={{ native: true }}
            sx={{ minWidth: 180 }}
          >
            <option value="">Todas</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </TextField>
          <TextField
            size="small"
            type="date"
            label="Desde"
            value={filters.date_from}
            onChange={(event) => setFilters((prev) => ({ ...prev, date_from: event.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="Hasta"
            value={filters.date_to}
            onChange={(event) => setFilters((prev) => ({ ...prev, date_to: event.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />} onClick={fetchData} disabled={loading}>
            Consultar
          </Button>
          <Button variant="outlined" startIcon={<ClearAll />} onClick={clear}>
            Limpiar
          </Button>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
        <Chip label={mode === 'MIXTO' ? 'Modo mixto' : 'Modo homogeneo'} color={mode === 'MIXTO' ? 'warning' : 'success'} variant="outlined" />
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {products.length} productos seleccionados
        </Typography>
      </Stack>

      {payload.warning ? <Alert severity="warning" sx={{ mt: 1 }}>{payload.warning}</Alert> : null}

      <Box sx={{ mt: 1.5, display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' } }}>
        <Metric label="CANTIDAD RECIBIDA" value={summary.total_quantity == null ? 'Separada' : number(summary.total_quantity)} />
        <Metric label="COSTO PROMEDIO" value={summary.average_cost == null ? 'Separado' : money(summary.average_cost)} />
        <Metric label="VARIACION COSTO" value={money(summary.cost_variation)} />
        <Metric label="FACTURAS" value={number(summary.invoice_count)} />
        <Metric label="MOVIMIENTOS" value={number(summary.movement_count)} />
      </Box>

      <Paper variant="outlined" sx={{ mt: 1.5, p: 1, borderRadius: 1 }}>
        <Typography sx={{ px: 1, py: 0.75, fontSize: 13, fontWeight: 700 }}>Totales por producto/presentacion</Typography>
        <Box sx={{ height: 280 }}>
          <DataGrid rows={payload.groups || []} columns={groupColumns} getRowId={(row) => `${row.product_id}-${row.presentation}-${row.unit}`} disableRowSelectionOnClick pageSizeOptions={[5, 10, 25]} />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ mt: 1.5, p: 1, borderRadius: 1 }}>
        <Typography sx={{ px: 1, py: 0.75, fontSize: 13, fontWeight: 700 }}>Movimientos</Typography>
        <Box sx={{ height: 420 }}>
          <DataGrid rows={payload.movements || []} columns={movementColumns} loading={loading} disableRowSelectionOnClick pageSizeOptions={[10, 25, 50]} />
        </Box>
      </Paper>

      <CustomSnackBar
        open={snack.open}
        setOpen={(open) => setSnack((prev) => ({ ...prev, open }))}
        message={snack.message}
        severity={snack.severity}
      />
    </>
  );
}
